/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, atom } from 'jotai';
import { DashboardType } from 'jotai/dashboard';
import { atomFamily, unwrap } from 'jotai/utils';
import {
  Event,
  EventSessionDetailsResponse,
  LineCrossing,
  QualifyingMetaData,
  RunStatus,
  RunType,
  SeriesType,
  Session,
  Track,
} from 'models/viewModels';
import { sessionService } from 'services/session/SessionService';

export const activeSessionIdAtom = atom<string>('');

export const activeSessionAtom = atom<EventSessionDetailsResponse | undefined>(undefined);

export const sessionsSelectorFamily = atomFamily<string, Atom<Promise<Session[]>>>((eventId: string) =>
  atom<Promise<Session[]>>(async (_get) => {
    const value = eventId ? await sessionService.getSessions(eventId) : [];
    return value;
  })
);

const activeSessionResponseSelector = atom<Promise<EventSessionDetailsResponse | undefined>>(async (get) => {
  const activeSession = get(activeSessionAtom);
  if (!activeSession) {
    const sessionId = get(activeSessionIdAtom);
    return await sessionService.getSession(sessionId);
  }
  return activeSession;
});

export const unwrappedActiveSessionResponseSelector = unwrap(activeSessionResponseSelector);

export const activeSessionSelector = atom<Session | undefined>((get) => {
  const response = get(unwrappedActiveSessionResponseSelector)?.session;
  return response;
});

export const activeEventSelector = atom<Event | undefined>((get) => {
  return get(unwrappedActiveSessionResponseSelector)?.event;
});

export const activeTrackSelector = atom<Track | undefined>((get) => {
  return get(unwrappedActiveSessionResponseSelector)?.track;
});

export const activeQualifyingMetaDataBySeries = atom<QualifyingMetaData | undefined>((get) => {
  return get(activeTrackSelector)?.qualifyingmetadataList.find((item) => item.seriesId === get(activeSeriesIdSelector));
});

export const activeTrackLineCrossingsSelector = atom<LineCrossing[]>((get) => {
  return get(unwrappedActiveSessionResponseSelector)?.track?.trackLineCrossingList || [];
});

export const activeSeriesIdSelector = atom<string | undefined>((get) => {
  return get(activeSessionSelector)?.seriesId;
});

export const activeSessionReplayEnabledSelector = atom<boolean>((get) => {
  return get(activeSessionSelector)?.sessionStatus === RunStatus.REPLAY_ENABLED || false;
});

export const raceSessionByTrackEventIdSelectorFamily = atomFamily<
  string | undefined,
  Atom<Promise<EventSessionDetailsResponse | undefined>>
>((trackEventId?: string) =>
  atom<Promise<EventSessionDetailsResponse | undefined>>(async () =>
    trackEventId !== undefined ? await sessionService.getRaceSessionByTrackEventId(trackEventId) : undefined
  )
);

export const dashboardTypeAtom = atom<DashboardType | ''>('');

export const isGroupedSessionSelector = atom<boolean>((get) => {
  const activeSession = get(activeSessionSelector);
  return (
    Number(activeSession?.seriesId) === SeriesType.CUP &&
    (activeSession?.sessionType === RunType.QUALIFYING || activeSession?.sessionType === RunType.PRACTICE)
  );
});

export const isNascarSessionSelector = atom<boolean>((get) =>
  [SeriesType.CUP, SeriesType.XFINITY, SeriesType.TRUCK]
    .map((it) => it.toString())
    .includes(get(activeSessionAtom)?.session.seriesId ?? '-1')
);

export const isIndySessionAtom = atom<boolean>(false);
